import { FC, createContext, useContext, useMemo, useCallback } from 'react';

import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { IShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.types';
import {
  shiftModalRoutingService,
  useShiftModalUIContext,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { useSelector } from 'react-redux';
import { useIsJaneCommunityMismatch } from 'hooks/useIsJaneCommunityMismatch/useIsJaneCommunityMismatch';
import { useLocation } from 'react-router-dom';
import { PAGE_URLS } from 'components/BKJMainNavigation/BKJMainNavigation.constants';
import { queryRefetchService } from 'services/QueryRefetchService';
import { createApolloClient, defaultApolloClient } from 'providers/ApolloProvider';
import { Jane } from 'types/graphql';
import { useFetchScheduleLinesVariables } from 'pages/Schedule/Schedule.hooks';
import { useScheduleLinesStore } from 'pages/Schedule/store/scheduleLines.store';
import isLineSchedulingSelector from 'store/selectors/scheduleSelectors/isLineSchedulingSelector';

export const ShiftModalPropsContext = createContext<IShiftModalPropsContext>(
  {} as IShiftModalPropsContext,
);

const apolloClient = createApolloClient({});

export const ShiftModalPropsProvider: FC = ({ children }) => {
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { pathname } = useLocation();
  const { shift_id: orderId } = useShiftModalUIContext();

  const isLineScheduling = useSelector(isLineSchedulingSelector);

  const variables = useFetchScheduleLinesVariables();
  const { fetchData } = useScheduleLinesStore();

  const currentlySelectedLocation = getCurrentlySelectedLocation();

  const isMultiLocationCallOutFeatureEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationSchedulingFeatureEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationTieringFeatureEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const mainJane = (order?.mainJane as Jane) ?? null;
  const orderCommunityOrgId = order?.community?.organization?.id;

  const isCommunityMismatch = useIsJaneCommunityMismatch({
    acceptedBySharedTeamMember: order?.acceptedBySharedTeamMember ?? null,
    status: order?.status!,
    mainJane,
    sentToAgencies: order?.sentToAgencies,
    orderCommunityOrgId,
    currentlySelectedLocation,
    isMultiLocationCallOutFeatureEnabled,
    isMultiLocationSchedulingFeatureEnabled,
    isMultiLocationTieringFeatureEnabled,
  });

  const isSharedJanePrimaryCommunityDifferentFromSelectedLocation = useMemo(
    () => (data ? isCommunityMismatch : false),
    [data, isCommunityMismatch],
  );

  const refreshBackgroundPage = useCallback(
    (all = false) => {
      if (pathname.startsWith(PAGE_URLS.SCHEDULE)) {
        window.scheduleUtils.forceDataUpdate();
        if (isLineScheduling) {
          fetchData(apolloClient, variables);
        }
      } else if (pathname.startsWith(PAGE_URLS.SHIFTS)) {
        defaultApolloClient.refetchQueries({ include: 'all' });
      } else if (pathname.startsWith(PAGE_URLS.DASHBOARD)) {
        if (orderId) {
          queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${orderId}`);
        }
        if (all) {
          if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/available`)) {
            queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE', 'available');
          } else if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/myupcoming`)) {
            queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE', 'myupcoming');
          } else if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/past`)) {
            queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE', 'past');
          } else if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/recent`)) {
            queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE', 'recent');
          } else if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/upcoming`)) {
            queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE', 'upcoming');
          }
        }
        if (pathname.startsWith(`${PAGE_URLS.DASHBOARD}/overview`)) {
          queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE_OVERVIEW', 'WEEKLY_OVERVIEW');
          queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE_OVERVIEW', 'NEEDS_REVIEW');
          queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE_OVERVIEW', 'NEEDS_ATTENTION');
          queryRefetchService.refetchDataQueryByKey(
            'DASHBOARD_PAGE_OVERVIEW',
            'NEEDS_ATTENTION_UPCOMING_SHIFTS',
          );
          queryRefetchService.refetchDataQueryByKey(
            'DASHBOARD_PAGE_OVERVIEW',
            'NEEDS_ATTENTION_BIDDING_SHIFTS',
          );
          queryRefetchService.refetchDataQueryByKey('DASHBOARD_PAGE_OVERVIEW', 'TEAM_MEMBERS_TAB');
        }
      } else if (pathname.startsWith(PAGE_URLS.TIMESHEETS)) {
        if (orderId && order?.orderSchedule?.timeSheet?.id)
          queryRefetchService.refetchDataQueryByKey(
            'TIMESHEETS_CARD',
            `${order?.orderSchedule?.timeSheet?.id}`,
          );
      }
    },
    [
      order?.orderSchedule?.timeSheet?.id,
      fetchData,
      isLineScheduling,
      orderId,
      pathname,
      variables,
    ],
  );

  const contextValue = useMemo(
    () => ({
      isSharedJanePrimaryCommunityDifferentFromSelectedLocation,
      goToDeleteView: () => shiftModalRoutingService.goToView('DeleteView'),
      goToEditView: () => shiftModalRoutingService.goToView('EditView'),
      goToReleaseShiftView: () => shiftModalRoutingService.goToView('ReleaseShiftView'),
      goToSendToAgenciesView: () =>
        order?.biddable && order?.autoRebid
          ? shiftModalRoutingService.goToView('SendToAgencyConfirmationView')
          : shiftModalRoutingService.goToView('PreRequestOptionsView'),
      goToAwardTeamMemberView: () => shiftModalRoutingService.goToView('AwardTeamMemberView'),
      goToConfirmEditsView: () => shiftModalRoutingService.goToView('ConfirmEditsView'),
      refreshBackgroundPage,
    }),
    [
      order?.autoRebid,
      order?.biddable,
      isSharedJanePrimaryCommunityDifferentFromSelectedLocation,
      refreshBackgroundPage,
    ],
  );
  return (
    <ShiftModalPropsContext.Provider value={contextValue}>
      {children}
    </ShiftModalPropsContext.Provider>
  );
};

export const useShiftModalPropsContext = (): IShiftModalPropsContext =>
  useContext(ShiftModalPropsContext);
